import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import { UsersTable } from "../components/UsersTable";
import { UsersFilters } from "../components/UsersFilters";
import { useUsers } from "../api/users";
import { UsersApiParams } from "../types/user";

const UsersPage: React.FC = () => {
    const { t } = useTranslation("users");
    const [searchParams, setSearchParams] = useSearchParams();

    // Инициализация фильтров из URL-параметров
    const getInitialFilters = (): UsersApiParams => {
        const params: UsersApiParams = {
            per_page: 20,
            page: 1,
        };

        const accessStatus = searchParams.get("access_status");
        if (accessStatus !== null) {
            params.access_status = accessStatus;
        }

        const accountType = searchParams.get("account_type");
        if (accountType !== null) {
            params.account_type = accountType;
        }

        const memberFeeFilter = searchParams.get("member_fee_filter");
        if (memberFeeFilter) {
            params.member_fee_filter = memberFeeFilter;
        }

        const search = searchParams.get("search");
        if (search) {
            params.search = search;
        }

        const role = searchParams.get("role");
        if (role) {
            params.role = role;
        }

        const country = searchParams.get("country");
        if (country) {
            params.country = country;
        }

        const sex = searchParams.get("sex") as "m" | "f" | "other" | null;
        if (sex) {
            params.sex = sex;
        }

        return params;
    };

    const [filters, setFilters] = useState<UsersApiParams>(getInitialFilters);

    // Обновляем URL при изменении фильтров
    useEffect(() => {
        const params = new URLSearchParams();

        if (filters.access_status !== undefined) {
            params.set("access_status", filters.access_status.toString());
        }
        if (filters.account_type !== undefined) {
            params.set("account_type", filters.account_type.toString());
        }
        if (filters.member_fee_filter) {
            params.set("member_fee_filter", filters.member_fee_filter);
        }
        if (filters.search) {
            params.set("search", filters.search);
        }
        if (filters.role) {
            params.set("role", filters.role);
        }
        if (filters.country) {
            params.set("country", filters.country);
        }
        if (filters.sex) {
            params.set("sex", filters.sex);
        }

        setSearchParams(params, { replace: true });
    }, [filters, setSearchParams]);

    const handleSortChange = (sortBy: string, sortOrder: "asc" | "desc") => {
        setFilters((prev) => ({
            ...prev,
            sort_by: sortBy,
            sort_order: sortOrder,
            page: 1, // Сбрасываем на первую страницу при изменении сортировки
        }));
    };

    const { data, isLoading, error } = useUsers(filters);

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handlePageSizeChange = (per_page: number) => {
        setFilters((prev) => ({ ...prev, per_page, page: 1 })); // Сбрасываем на первую страницу
    };

    if (error) {
        return (
            <ProtectedRoute requiredRoles={["admin"]}>
                <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <div className="text-center">
                            <div className="text-red-600 dark:text-red-400 text-lg font-medium mb-2">
                                {t("error.title")}
                            </div>
                            <div className="text-gray-600 dark:text-gray-400">
                                {t("error.message")}
                            </div>
                        </div>
                    </div>
                </div>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute requiredRoles={["admin"]}>
            <div className="h-screen flex flex-col">
                {/* Фиксированный заголовок */}
                <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {t("title")}
                        </h2>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {data?.meta?.total && (
                                <span>
                                    {t("total", { count: data.meta.total })}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Фиксированные фильтры */}
                <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-6">
                    <UsersFilters
                        filters={filters}
                        onFiltersChange={setFilters}
                    />
                </div>

                {/* Прокручиваемая таблица */}
                <div className="flex-1">
                    <UsersTable
                        data={data?.data || []}
                        isLoading={isLoading}
                        pagination={
                            data?.meta || {
                                current_page: 1,
                                last_page: 1,
                                per_page: 20,
                                total: 0,
                            }
                        }
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSortChange={handleSortChange}
                        currentSort={{
                            sort_by: filters.sort_by,
                            sort_order: filters.sort_order,
                        }}
                        accessStatusFilter={filters.access_status}
                    />
                </div>
            </div>
        </ProtectedRoute>
    );
};

export default UsersPage;
